"use client"

import type { ReactNode } from "react"
import { useMemo } from "react"
import { usePathname } from "next/navigation"

import type { StorefrontNavbarPosition } from "@lib/data/storefront-components"

type MainContentOffsetProps = {
  navbarPosition: StorefrontNavbarPosition
  children: ReactNode
}

const normalizePathname = (value: string) => {
  if (!value) return "/"
  if (value === "/") return value
  return value.endsWith("/") ? value.slice(0, -1) : value
}

const isProductDetailPath = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean)
  const productsIndex = segments.indexOf("products")
  return productsIndex >= 0 && productsIndex < segments.length - 1
}

export default function MainContentOffset({
  navbarPosition,
  children,
}: MainContentOffsetProps) {
  const pathname = usePathname()

  const applyFixedOffset = useMemo(() => {
    if (navbarPosition !== "fixed") return false

    const normalizedPathname = normalizePathname(pathname || "/")

    if (normalizedPathname === "/") {
      return false
    }

    if (isProductDetailPath(normalizedPathname)) {
      return false
    }

    return true
  }, [navbarPosition, pathname])

  return (
    <div
      className={
        applyFixedOffset
          ? "storefront-main-content-with-fixed-offset"
          : undefined
      }
    >
      {children}
    </div>
  )
}
